/**
 * Lists every AI overview shared to a given requisition.
 *
 * Recruiters + hiring managers see the candidate name, who shared it, when,
 * any notes, and an expandable Markdown body. Backed by:
 *   GET /api/cockpit/requisitions/{id}/ai-overviews
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, ChevronDown, ChevronUp, Copy, Check, RefreshCw, Hash } from 'lucide-react';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(ts) {
  if (!ts) return '';
  try { return new Date(ts).toLocaleString(); } catch { return ts; }
}

function ShareRow({ row }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!row.ai_profile_content) return;
    try {
      await navigator.clipboard.writeText(row.ai_profile_content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard API unavailable (insecure context or denied permission).
      // The expandable body still shows the text for manual copying.
      logger.warn('ai-overview clipboard copy failed', e?.message);
    }
  };

  return (
    <li className="rounded-xl border border-[var(--vh-border)] bg-[var(--vh-bg-surface)]" data-testid={`req-ai-overview-${row.id}`}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left p-4 flex flex-wrap items-start justify-between gap-3"
        aria-expanded={open}
        data-testid={`req-ai-overview-toggle-${row.id}`}
      >
        <div className="min-w-0">
          <p className="text-sm font-medium text-[var(--vh-text-primary)] truncate">
            {row.candidate_name || 'Candidate'}
            {row.ai_profile_target_role && (
              <span className="text-[var(--vh-text-muted)] font-normal"> · {row.ai_profile_target_role}</span>
            )}
          </p>
          <p className="text-xs text-[var(--vh-text-muted)] mt-0.5">
            Shared by {row.shared_by_name || 'a recruiter'} · {fmt(row.created_at)}
          </p>
          {row.notes && (
            <p className="text-sm text-[var(--vh-text-secondary)] mt-2 italic">“{row.notes}”</p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {row.ai_profile_content_hash && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[var(--vh-bg-muted)] text-[10px] uppercase tracking-wider text-[var(--vh-text-muted)]" title={row.ai_profile_content_hash}>
              <Hash className="w-3 h-3" />{row.ai_profile_content_hash.slice(0, 8)}
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-[var(--vh-text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--vh-text-muted)]" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-[var(--vh-border)] p-4 bg-[var(--vh-bg-muted)]" data-testid={`req-ai-overview-body-${row.id}`}>
          {row.ai_profile_content ? (
            <>
              <pre className="whitespace-pre-wrap text-sm text-[var(--vh-text-primary)] leading-relaxed font-sans">{row.ai_profile_content}</pre>
              <div className="mt-3 flex items-center justify-end">
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--vh-brand-primary)] hover:bg-[var(--vh-brand-primary-hover)] text-white text-xs"
                  data-testid={`req-ai-overview-copy-${row.id}`}
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied' : 'Copy text'}
                </button>
              </div>
            </>
          ) : (
            <p className="text-sm text-[var(--vh-text-muted)]">No content saved for this share.</p>
          )}
        </div>
      )}
    </li>
  );
}

export default function RequisitionAIOverviews({ requisitionId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!requisitionId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${API}/cockpit/requisitions/${requisitionId}/ai-overviews`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load AI overviews.');
    } finally {
      setLoading(false);
    }
  }, [requisitionId]);

  useEffect(() => { load(); }, [load]);

  if (!requisitionId) return null;

  return (
    <section
      className="mb-4 rounded-2xl bg-[var(--vh-bg-surface)] border border-[var(--vh-border)] p-4"
      data-testid="req-ai-overviews-panel"
    >
      <header className="flex items-center justify-between mb-3">
        <h3 className="inline-flex items-center gap-2 font-display text-base text-[var(--vh-text-primary)]">
          <Sparkles className="w-4 h-4 text-[var(--vh-accent)]" />
          AI overviews attached to this requisition
          <span className="ml-2 px-2 py-0.5 rounded-full bg-[var(--vh-bg-muted)] text-xs text-[var(--vh-text-secondary)]" data-testid="req-ai-overviews-count">{rows.length}</span>
        </h3>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full border border-[var(--vh-border)] text-xs text-[var(--vh-text-secondary)] hover:bg-[var(--vh-bg-muted)] disabled:opacity-60"
          data-testid="req-ai-overviews-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </header>

      {error && <p className="text-sm text-red-700 mb-2" data-testid="req-ai-overviews-error">{error}</p>}

      {loading && rows.length === 0 ? (
        <p className="text-sm text-[var(--vh-text-muted)]" data-testid="req-ai-overviews-loading">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-[var(--vh-text-muted)]" data-testid="req-ai-overviews-empty">
          No AI overviews shared to this requisition yet. Recruiters can push a candidate’s approved AI profile from the candidate sheet.
        </p>
      ) : (
        <ul className="space-y-2" data-testid="req-ai-overviews-list">
          {rows.map((r) => <ShareRow key={r.id} row={r} />)}
        </ul>
      )}
    </section>
  );
}
